import Captcha from "components/UI/Captcha";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { login } from "services/Auth";
import { BaseButton } from "components/buttons/BaseButton";
import { AxiosError } from "axios";
import { IErrorData } from "services/types";
import { TextInput } from "components/inputs/TextInput/TextInput";
import { AuthPopupHelpButton } from "../AuthPopupHelpButton";
import { TogglePasswordButton } from "components/buttons/TogglePasswordButton";
import authPopupClasses from "./../AuthPopup.module.scss";

interface IProps {
  next: () => void;
}

const LoginWithEmail = ({ next }: IProps) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [resetCaptcha, setResetCaptcha] = useState<(() => void) | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>("");
  const [passwordError, setPasswordError] = useState<string | null>("");
  const [captchaError, setCaptchaError] = useState<string | null>("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    validate();

    if (!isValid()) {
      return;
    }

    setError("");
    setIsSending(true);

    try {
      await login({ username, password, captcha });
    } catch (e) {
      const message = (e as AxiosError<IErrorData>)?.response?.data?.error?.message || "Service unavailable";
      setError(message);
    } finally {
      setIsSending(false);
      handleCaptchaReset();
      if (resetCaptcha !== null) {
        resetCaptcha();
      }
    }
  };

  const validateUsername = useCallback((value: string) => {
    setUsernameError(value.length === 0 ? "Username required" : null);
  }, []);

  const validatePassword = useCallback((value: string) => {
    setPasswordError(value.length === 0 ? "Password required" : null);
  }, []);

  const validateCaptcha = useCallback((value: string) => {
    setCaptchaError(value.length === 0 ? "Captcha required" : null);
  }, []);

  const validate = useCallback(
    () => {
      validateUsername(username);
      validatePassword(password);
      validateCaptcha(captcha);
    },
    [
      validateUsername,
      validatePassword,
      validateCaptcha,
      username,
      password,
      captcha,
    ]
  );

  const isValid = useCallback(
    () => usernameError === null && passwordError === null && captchaError === null,
    [usernameError, passwordError, captchaError]
  );

  const handleChangeUsername = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    validateUsername(e.target.value);
  }, [validateUsername]);

  const handleChangePassword = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    validatePassword(e.target.value);
  }, [validatePassword]);

  const handleCaptchaSuccess = useCallback((token: string) => {
    setCaptcha(token);
    validateCaptcha(token);
  }, [validateCaptcha]);

  const handleCaptchaReset = useCallback(() => {
    setCaptcha("");
    validateCaptcha("");
  }, [validateCaptcha]);

  const handleCaptchaRendered = useCallback((reset: () => void) => {
    setResetCaptcha(() => reset);
  }, []);

  const handleResetPassword = () => {
    next();
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className={authPopupClasses.ContentContainer}>
          <TextInput
            label="Email or User Name"
            onChange={handleChangeUsername}
            value={username}
            error={usernameError === null ? undefined : usernameError}
            autoFocus={true}
          />

          <TextInput
            label="Password"
            type={showPassword ? "text" : "password"}
            onChange={handleChangePassword}
            value={password}
            error={passwordError === null ? undefined : passwordError}
            rightComponent={
              <TogglePasswordButton onClick={() => setShowPassword(prev => !prev)} isShow={showPassword}/>
            }
          />

          <AuthPopupHelpButton onClick={handleResetPassword} value="Forgot password?" />

          <Captcha
            id="captcha"
            onSuccess={handleCaptchaSuccess}
            onFailed={handleCaptchaReset}
            onRendered={handleCaptchaRendered}
          />

          { error &&
              <div className={authPopupClasses.ErrorMessage}>
                { error }
              </div>
          }
        </div>

        <div className={authPopupClasses.Footer}>
          <BaseButton type="submit" disabled={!isValid() || isSending}>
            Log in
          </BaseButton>
        </div>
      </form>
    </div>
  );
};

export default LoginWithEmail;
